/* ==========================================================================
   NUKKA — global.js
   Shared utilities, drawer controller, sticky header, quantity stepper,
   off-canvas menu, and a tiny init bus that re-runs in the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  window.NUKKA = window.NUKKA || {};
  var N = window.NUKKA;

  /* ---------- Helpers ---------- */
  N.on = function (el, evt, sel, handler) {
    el.addEventListener(evt, function (e) {
      var t = e.target.closest(sel);
      if (t && el.contains(t)) handler(e, t);
    });
  };
  N.formatMoney = function (cents) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    var fmt = N.moneyFormat || '₹{{amount}}';
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };
  N.fetchConfig = function (type) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/' + (type || 'json') }
    };
  };

  /* Init bus: register a fn, runs now + on every section load/select in editor */
  var modules = [];
  N.register = function (fn) {
    modules.push(fn);
    if (document.readyState !== 'loading') fn(document);
  };
  function runAll(scope) { modules.forEach(function (fn) { try { fn(scope); } catch (e) { console.error(e); } }); }
  // For HTML injected after load (e.g. async product recommendations).
  N.initScope = runAll;
  document.addEventListener('DOMContentLoaded', function () { runAll(document); });
  document.addEventListener('shopify:section:load', function (e) { runAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { runAll(e.target); });

  /* ---------- Scroll lock ---------- */
  var locks = 0;
  N.lockScroll = function () { locks++; document.body.classList.add('scroll-lock'); };
  N.unlockScroll = function () { locks = Math.max(0, locks - 1); if (!locks) document.body.classList.remove('scroll-lock'); };

  /* ---------- Generic drawer controller ---------- */
  N.Drawer = {
    open: function (id, trigger) {
      var el = document.getElementById(id);
      if (!el) return;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      N.lockScroll();
      el._trigger = trigger || document.activeElement;
      var focusTarget = el.querySelector('[autofocus], input, button, a, [tabindex]');
      if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 60);
      document.dispatchEvent(new CustomEvent('nukka:drawer:open', { detail: { id: id } }));
    },
    close: function (id) {
      var el = typeof id === 'string' ? document.getElementById(id) : id;
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      N.unlockScroll();
      if (el._trigger && el._trigger.focus) el._trigger.focus();
    },
    closeAll: function () {
      document.querySelectorAll('.drawer.is-open').forEach(function (d) { N.Drawer.close(d); });
    }
  };

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); N.Drawer.open(opener.getAttribute('data-drawer-open'), opener); return; }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); N.Drawer.close(closer.closest('.drawer')); }
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') N.Drawer.closeAll(); });

  /* Focus trap within open drawers */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var open = document.querySelector('.drawer.is-open');
    if (!open) return;
    var focusables = open.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------- Sticky header shadow ---------- */
  N.register(function () {
    var header = document.querySelector('.header');
    if (!header || header._sticky) return;
    header._sticky = true;
    var onScroll = function () { header.classList.toggle('header--scrolled', window.scrollY > 8); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });

  /* ---------- Quantity stepper (custom element) ---------- */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-qty-input]');
        if (!this.input) return;
        var minus = this.querySelector('[data-qty-minus]');
        var plus = this.querySelector('[data-qty-plus]');
        if (minus) minus.addEventListener('click', this.step.bind(this, -1));
        if (plus) plus.addEventListener('click', this.step.bind(this, 1));
        this.input.addEventListener('change', function () {
          this.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true, detail: { value: this.value } }));
        }.bind(this.input));
      }
      step(dir, e) {
        e.preventDefault();
        var min = parseInt(this.input.min || '1', 10);
        var val = parseInt(this.input.value || min, 10) + dir;
        if (val < min) val = min;
        this.input.value = val;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ---------- Scroll-linked starburst rotation (clockwise, eased) ---------- */
  N.register(function () {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var els = document.querySelectorAll('[data-scroll-rotate]');
    if (!els.length || document._scrollRotate) return;
    document._scrollRotate = true;

    var factor = 0.25;                 // degrees per pixel scrolled (clockwise)
    var ease = 0.09;                   // lower = smoother / more "inertia"
    var current = window.scrollY * factor;
    var raf = null;

    function apply() {
      document.querySelectorAll('[data-scroll-rotate]').forEach(function (el) {
        var dir = el.getAttribute('data-scroll-rotate') === 'reverse' ? -1 : 1;
        el.style.setProperty('--sb-scroll', (current * dir).toFixed(2) + 'deg');
      });
    }
    function loop() {
      var target = window.scrollY * factor;
      current += (target - current) * ease;     // lerp toward scroll position
      apply();
      if (Math.abs(target - current) > 0.05) {
        raf = requestAnimationFrame(loop);
      } else {
        current = target; apply(); raf = null;  // settle, then idle
      }
    }
    window.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });
    apply();
  });

  /* ---------- Scroll-reveal + count-up ---------- */
  N.register(function (scope) {
    var els = scope.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function setCount(node, val, suffix) { node.textContent = Math.round(val) + (suffix || ''); }
    function countUp(el) {
      el.querySelectorAll('[data-count]').forEach(function (node) {
        var target = parseFloat(node.getAttribute('data-count'));
        if (isNaN(target)) return;
        var suffix = node.getAttribute('data-suffix') || '';
        var dur = 1400, start = performance.now();
        (function tick(now) {
          var p = Math.min(1, (now - start) / dur);
          setCount(node, target * (1 - Math.pow(1 - p, 3)), suffix);
          if (p < 1) requestAnimationFrame(tick);
        })(start);
      });
    }

    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (e) {
        e.classList.add('reveal-in');
        e.querySelectorAll('[data-count]').forEach(function (n) { setCount(n, parseFloat(n.getAttribute('data-count')) || 0, n.getAttribute('data-suffix') || ''); });
      });
      return;
    }
    if (!N._revealObs) {
      N._revealObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add('reveal-in');
            countUp(en.target);
            N._revealObs.unobserve(en.target);
          }
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    }
    els.forEach(function (e) { if (!e._revObserved) { e._revObserved = true; N._revealObs.observe(e); } });
  });

  /* ---------- Collection filter dropdowns: close on outside click / Esc, one open at a time ---------- */
  document.addEventListener('click', function (e) {
    document.querySelectorAll('.facets details[open]').forEach(function (d) {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.facets details[open]').forEach(function (d) { d.removeAttribute('open'); });
  });

  /* ---------- Dropdown keyboard accessibility ---------- */
  N.register(function (scope) {
    scope.querySelectorAll('.has-dropdown > button').forEach(function (btn) {
      if (btn._dd) return; btn._dd = true;
      btn.addEventListener('click', function () {
        var dd = btn.nextElementSibling;
        var open = dd.getAttribute('data-open') === 'true';
        dd.setAttribute('data-open', String(!open));
        btn.setAttribute('aria-expanded', String(!open));
      });
    });
  });
})();
